import { ScrollArea } from "@/components/primitives/scroll-area";
import { RefObject } from "react";

/**
 * Container component for links
 * Provides scrolling functionality and layout for the link list
 *
 * How it works:
 * 1. Creates a scrollable container with proper overflow handling
 * 2. Accepts and forwards ref objects for scroll position management
 * 3. Renders children components within the scrollable area
 * 4. Uses flex-col-reverse to position newest content at the bottom
 *
 * Key features:
 * - Touch-friendly scrolling with WebkitOverflowScrolling
 * - Consistent scrolling behavior across devices
 * - Proper overflow handling and containment
 * - Fade-in animation for smooth appearance
 * - Maintains scroll position during content updates
 *
 * Context:
 * This component is used by LinkList to provide the scrollable
 * container for all links. It follows the same pattern as
 * MediaAttachmentContainer and FileAttachmentContainer for consistency.
 */
interface LinkContainerProps {
  scrollerRef: RefObject<HTMLDivElement>;
  linksListRef: RefObject<HTMLDivElement>;
  children: React.ReactNode;
}

export default function LinkContainer({
  scrollerRef,
  linksListRef,
  children,
}: LinkContainerProps) {
  return (
    <div
      ref={scrollerRef}
      style={{ WebkitOverflowScrolling: "touch" }}
      className="relative size-full flex-1 flex-col justify-end overflow-y-auto overflow-x-hidden fade-in"
    >
      <ScrollArea className="h-full flex-1 p-1">
        <div
          ref={linksListRef}
          style={{ WebkitOverflowScrolling: "touch" }}
          className="custom-scroll custom-scroll-x relative flex size-full flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
        >
          {children}
        </div>
      </ScrollArea>
    </div>
  );
}
